import React, { useRef } from 'react';
import { Platform } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  HeaderText,
  Stack,
  Button,
} from '../../../ui';
import QueryFilterContainer from '../../../consumer/search/QueryFilter';
import { useIsMobile } from '../../../../hooks';
import { SearchTiles } from './SearchTiles';
import { AUTO_SUGGEST_Z_INDEX } from './constants';
import { BasicSearchIcon } from '../../../ui/Icons';
import SearchInputModalTrigger from '../../../consumer/search/SearchPopover/SearchInputModalTrigger';
import { LocationFilter } from '../../../consumer/search/LocationFilter';
import { LocationRecord } from '../../../../store/Location.model';
import { ISearchTile } from '../../homepage/SearchTile/SearchTile.types';
import { SEARCH_ROUTE } from '../../../../route-names';

type FindSomeoneNewProps = {
  tiles: ISearchTile[];
  onTileClick: Parameters<typeof SearchTiles>[0]['onTileClick'];
  searchParams: any;
  location?: LocationRecord;
  onUpdateLocation: (event: LocationRecord) => void;
  onUpdateQuery: (queryText: string) => void;
  onSubmit: (queryText: string) => void;
  title?: React.ReactNode;
};

export const FindSomeoneNewSection = ({
  tiles,
  onTileClick,
  searchParams,
  location,
  onUpdateLocation,
  onUpdateQuery,
  onSubmit,
  title = 'Read reviews, browse photos, find your perfect service',
}: FindSomeoneNewProps) => {
  const isMobile = useIsMobile();
  const locationRef = useRef<any>(null);

  const handleQueryBlur = () => {
    if (locationRef.current && !location?.address) {
      locationRef.current.input.focus();
    }
  };

  return (
    <Stack
      testID="inactive-profile-find-someone-new"
      alignItems={isMobile ? 'stretch' : 'center'}
      style={{ zIndex: AUTO_SUGGEST_Z_INDEX }}
      flexShrink={1}
      color={BrandColor.BoneLight}
      paddingTop={isMobile ? 16 : 28}
      paddingBottom={isMobile ? 8 : 4}
    >
      <Box paddingX={36} paddingBottom={isMobile ? 8 : 20}>
        <HeaderText level={2} textAlign={isMobile ? 'center' : 'left'}>
          {title}
        </HeaderText>
      </Box>

      <Inline
        justifyContent="center"
        style={{ zIndex: AUTO_SUGGEST_Z_INDEX }}
        paddingBottom={isMobile ? 8 : 0}
        space={8}
        testID="inactive-profile-search"
        className="client-app"
      >
        {isMobile ? (
          <SearchInputModalTrigger source="inactive-profile" />
        ) : (
          <Inline alignItems="center" space={8}>
            <QueryFilterContainer
              setQuery={onSubmit}
              onClosePopover={onUpdateQuery}
              showSearchButton={false}
              hideSearchButtonInFocusedView
              onBlur={handleQueryBlur}
            />
            <LocationFilter
              address={location?.address || ''}
              updateLocation={onUpdateLocation}
              ref={locationRef}
            />
            <Button
              size="large"
              link={Platform.OS === 'web' ? SEARCH_ROUTE : null}
              appearance="primary"
              linkParams={searchParams}
              testID="find-someone-new-search"
              style={{ display: 'flex', padding: 8 }}
            >
              <BasicSearchIcon color={BrandColor.WhiteWash} />
            </Button>
          </Inline>
        )}
      </Inline>

      <Box paddingLeft={16} paddingBottom={isMobile ? 8 : 20} maxWidth="100%">
        <SearchTiles tiles={tiles} onTileClick={onTileClick} />
      </Box>
    </Stack>
  );
};
